/**
 * rescaleLineQuantity: the inline "Sqft: [n]" quantity edit (CommittedLineCard).
 *
 * IDI-310 (2026-09-29): the inline edit used to set ONLY `line.quantity`.
 * Everything that matters reads `sqft`:
 *   - the card itself (`item.sqft || item.quantity`), so a reload showed the
 *     OLD number and the edit looked lost;
 *   - pricing: materialCost / laborCost were never recomputed, so the price
 *     didn't move at all;
 *   - work-order materials (woMaterialsProjection reads line.sqft).
 * Measured live on migrated quote 1092: quantity 1250 -> 747 saved while
 * sqft stayed 1250 and the line total was unchanged.
 *
 * Rule: keep quantity and sqft in step, and scale the line's COSTS by
 * new/old. Proportional scaling preserves each line's OWN unit rates (the
 * migrated per-sqft material and labor rates, or whatever the catalog priced
 * at add time). It deliberately does NOT re-run catalog pricing: for a
 * migrated line that would silently re-price it at today's catalog, which is
 * exactly the "edited migrated lines carry wrong totals" risk the ticket
 * called out. A deliberate re-price is what the Edit wizard is for.
 *
 * Fixed per-line extras (additionalLabor / additionalCost) are NOT scaled.
 * They are flat add-ons, not per-unit.
 *
 * A line with no usable previous quantity (a custom item whose quantity was
 * never set; PR-19's original use of this setter) can't be scaled: we set
 * the quantity and leave costs as they are, which is the old behaviour.
 */
const COST_KEYS = ['materialCost', 'laborCost', 'otherCost'];

export default function rescaleLineQuantity(line, nextQty) {
  const next = Number(nextQty);
  if (!line || !Number.isFinite(next) || next < 0) return line;

  const prevQty = Number(line.sqft) || Number(line.quantity) || 0;
  const updated = { ...line, quantity: next };
  // Only lines that are measured in sqft carry the field; don't invent it on a
  // custom 'ea' line.
  if (line.sqft != null || prevQty > 0) updated.sqft = next;

  if (prevQty > 0) {
    const ratio = next / prevQty;
    for (const k of COST_KEYS) {
      if (typeof line[k] === 'number' && Number.isFinite(line[k])) {
        updated[k] = Math.round(line[k] * ratio);
      }
    }
    // A stored line total is scaled by the SAME ratio, never dropped. The
    // margin engine treats a stored total that doesn't match cost/(1-margin)
    // as a real price override. Migrated lines on quotes with no derivable
    // margin are stamped line_total = cost (0% margin, flattenAreaLineItems),
    // and dropping that would silently re-price them at the tenant's default
    // margin. Scaling cost and total together keeps the line's effective
    // margin exactly where it was.
    for (const k of ['line_total', 'lineTotal', 'cost_basis']) {
      if (typeof line[k] === 'number' && Number.isFinite(line[k])) {
        updated[k] = Math.round(line[k] * ratio);
      }
    }
    // unit_rate is per-unit, so it doesn't change with quantity.
  }
  return updated;
}
